import { createFileRoute, Link } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { z } from 'zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { AddressAutocomplete } from '@/components/AddressAutocomplete'
import { getParentByIdOptions, updateParentMutation } from '@/lib/client/@tanstack/react-query.gen'
import { ArrowLeft } from 'lucide-react'

export const Route = createFileRoute('/link/$parentId/profil')({
  component: ProfilPage,
})

function ProfilPage() {
  const { parentId } = Route.useParams()

  const { data: parent, isLoading, refetch } = useQuery(getParentByIdOptions({
    path: { id: parentId }
  }))

  const updateMutation = useMutation({
    ...updateParentMutation(),
    onSuccess: () => {
      toast.success('Vos informations ont été mises à jour')
      refetch()
    },
    onError: () => {
      toast.error('Erreur lors de la mise à jour')
    },
  })

  const form = useForm({
    defaultValues: {
      firstName: parent?.firstName || '',
      lastName: parent?.lastName || '',
      email: parent?.email || '',
      phone: parent?.phone || '',
      address: parent?.address || '',
      addressLatitude: parent?.addressLatitude,
      addressLongitude: parent?.addressLongitude,
    },
    onSubmit: async ({ value }) => {
      updateMutation.mutate({
        path: { id: parentId },
        body: {
          firstName: value.firstName,
          lastName: value.lastName,
          email: value.email,
          phone: value.phone,
          address: value.address,
          addressLatitude: value.addressLatitude,
          addressLongitude: value.addressLongitude,
        },
      })
    },
  })

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  if (parent && !form.state.values.firstName) {
    form.setFieldValue('firstName', parent.firstName)
    form.setFieldValue('lastName', parent.lastName)
    form.setFieldValue('email', parent.email)
    form.setFieldValue('phone', parent.phone)
    form.setFieldValue('address', parent.address)
    form.setFieldValue('addressLatitude', parent.addressLatitude)
    form.setFieldValue('addressLongitude', parent.addressLongitude)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/link/$parentId" params={{ parentId }}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour au tableau de bord
            </Link>
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Mon profil</h1>
          <p className="text-gray-600 mt-2">Gérer mes informations personnelles</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Informations personnelles</CardTitle>
            <CardDescription>
              Modifiez vos informations de contact
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                form.handleSubmit()
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <form.Field
                  name="firstName"
                  validators={{
                    onChange: ({ value }) => {
                      const result = z.string().min(1, 'Le prénom est requis').safeParse(value)
                      return result.success ? undefined : result.error.issues[0]?.message
                    },
                  }}
                >
                  {(field) => (
                    <div className="space-y-2">
                      <Label htmlFor="firstName">Prénom</Label>
                      <Input
                        id="firstName"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-sm text-red-500">
                          {field.state.meta.errors[0]}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>

                <form.Field
                  name="lastName"
                  validators={{
                    onChange: ({ value }) => {
                      const result = z.string().min(1, 'Le nom est requis').safeParse(value)
                      return result.success ? undefined : result.error.issues[0]?.message
                    },
                  }}
                >
                  {(field) => (
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Nom</Label>
                      <Input
                        id="lastName"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-sm text-red-500">
                          {field.state.meta.errors[0]}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>
              </div>

              <form.Field
                name="email"
                validators={{
                  onChange: ({ value }) => {
                    const result = z.string().email('Email invalide').safeParse(value)
                    return result.success ? undefined : result.error.issues[0]?.message
                  },
                }}
              >
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-red-500">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              <form.Field
                name="phone"
                validators={{
                  onChange: ({ value }) => {
                    const result = z.string().min(1, 'Le téléphone est requis').safeParse(value)
                    return result.success ? undefined : result.error.issues[0]?.message
                  },
                }}
              >
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-red-500">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              <form.Field
                name="address"
                validators={{
                  onChange: ({ value }) => {
                    const result = z.string().min(1, "L'adresse est requise").safeParse(value)
                    return result.success ? undefined : result.error.issues[0]?.message
                  },
                }}
              >
                {(field) => (
                  <div className="space-y-2">
                    <AddressAutocomplete
                      label="Adresse"
                      value={field.state.value}
                      onChange={(address, lat, lng) => {
                        field.handleChange(address)
                        if (lat && lng) {
                          form.setFieldValue('addressLatitude', lat)
                          form.setFieldValue('addressLongitude', lng)
                        }
                      }}
                      required
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-red-500">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              <Button
                type="submit"
                className="w-full"
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
